<template>
  <div class="actions-container">
    <component
      :is="getComponentName()"
      :is-opened="isOpened"
      @close="onActionCancelled"
    >
      <template #header>
        <form-stepper
          v-if="progressBar"
          :nb-step="nbStep"
          :actual-step="actualStep"
          :hidden="progressBarHidden"
          @previous="changeStep()"
        />
        <template v-else>
          {{ title }}
        </template>
      </template>
      <template #body>
        <visit-slot-choice
          v-if="componentIsVisitSlotChoice"
          :deal="deal"
          :interlocutor="interlocutor"
          @title-update="setTitle"
          @action-cancelled="onActionCancelled"
          @action-performed="onActionPerformed"
        />
        <deal-archive
          v-if="componentIsDealArchive"
          :deal="deal"
          :payload="payload"
          :action="currentAction"
          @title-update="setTitle"
          @action-cancelled="onActionCancelled"
          @action-performed="onActionPerformed"
        />
        <deal-in-progress
          v-if="componentIsDealInProgress"
          :deal="deal"
          :interlocutor="interlocutor"
          @title-update="setTitle"
          @action-cancelled="onActionCancelled"
          @action-performed="onActionPerformed"
        />
        <deal-signed
          v-if="componentIsDealSigned"
          :deal="deal"
          :interlocutor="interlocutor"
          @title-update="setTitle"
          @action-cancelled="onActionCancelled"
          @action-performed="onActionPerformed"
        />
        <deal-unsigned
          v-if="componentIsDealUnsigned"
          :deal="deal"
          :interlocutor="interlocutor"
          @title-update="setTitle"
          @action-cancelled="onActionCancelled"
          @action-performed="onActionPerformed"
        />
        <ask-feedback
          v-if="componentIsAskfeedback"
          :deal="deal"
          @set-title="setTitle"
          @set-progressbar="setProgressBar"
          @hide-progressbar="hideProgressBar"
          @close="close"
          @close-slide-dashboard="onActionPerformed"
        />
      </template>
    </component>
  </div>
</template>

<script>

import { eventBus } from '@tools/event-bus';
import { momentMixin } from '@tools/moment';
import SlideIn from '@components/Slidein.vue';
import SliderUp from '@components/Sliderup.vue';
import AskFeedback from '@pages/inbox/conversations-show/Actions/AskFeedback.vue';
import FormStepper from '@pages/inbox/conversations-show/FormStepper.vue';
import DealArchive from '../actions/Archive.vue';
import DealInProgress from '../actions/DealInProgress.vue';
import DealSigned from '../actions/DealSigned.vue';
import DealUnsigned from '../actions/DealUnsigned.vue';
import VisitSlotChoice from '../actions/VisitSlotChoice.vue';
import {
  COLUMN__IN_PROGRESS, COLUMN__SIGNED, COLUMN__UNSIGNED, COLUMN__VISITED, EVENTS as BOARD_EVENTS,
} from '../constants';
import { ACTIONS } from '../actions/constants';
import { CardActionPayload } from './CardActionPayload';

export default {
  components: { DealArchive, DealInProgress, DealSigned, DealUnsigned, VisitSlotChoice, AskFeedback, FormStepper, SlideIn, SliderUp },
  mixins: [ momentMixin ],
  props: {
    payload: { type: CardActionPayload, default: {}, required: false },
  },
  data() {
    return {
      currentAction: '',
      isOpened: false,
      title: '',
      actions: ACTIONS,
      progressBar: false,
      nbStep: 0,
      actualStep: 0,
      progressBarHidden: false,
    };
  },
  computed: {
    deal() {
      return this.payload.deal || {};
    },
    interlocutor() {
      return this.payload.interlocutor || {};
    },
    componentIsVisitSlotChoice() {
      return this.currentAction === this.actions.VISIT__SLOT_CHOICE;
    },
    componentIsAskfeedback() {
      return this.currentAction === this.actions.VISIT__FEEDBACK;
    },
    componentIsDealArchive() {
      return [ this.actions.DEAL__ARCHIVE, this.actions.DEAL__ARCHIVE_UI ].includes(this.currentAction);
    },
    componentIsDealInProgress() {
      return this.currentAction === this.actions.DEAL__IN_PROGRESS;
    },
    componentIsDealSigned() {
      return this.currentAction === this.actions.DEAL__SIGNED;
    },
    componentIsDealUnsigned() {
      return this.currentAction === this.actions.DEAL__UNSIGNED;
    },
  },
  watch: {
    payload(newPayload, oldPayload) { // eslint-disable-line no-unused-vars
      if (!newPayload) {
        this.close();
        return;
      }
      this.actionTriggered();
    },
  },
  methods: {
    getComponentName() {
      return document.body.classList.contains('is-mobile') ? 'slider-up' : 'slide-in';
    },
    onActionCancelled() {
      this.$emit(BOARD_EVENTS.CARD_ACTION_CANCELLED, this.payload);
      this.close();
    },
    /**
     * @param {CardActionPayload} payloadNext
     */
    onActionPerformed(payloadNext, data = null) {
      const { payload } = this;
      if (payloadNext) {
        payload.next = payloadNext;
      }

      // If we have moved a deal to "en cours" column
      if (this.componentIsDealInProgress && payload.deal.visited_date !== null && this.isBeforeToday(payload.deal.visited_date.iso)) {
        this.close();
        this.$emit('card-action-completed', payload);
        return;
      }

      if (this.componentIsVisitSlotChoice && data.visitDate && this.isBeforeToday(data.visitDate)) {
        this.$emit('card-action-completed', payload);
        return;
      }
      this.close();
      this.$emit('card-action-completed', payload);
    },
    actionTriggered() {
      this.currentAction = this.getAction();
      if (!this.currentAction) {
        // For now, if no action can be defined, we revert the card move.
        this.onActionCancelled();
        return;
      }
      // If the deal came from signed/unsigned, run the action without slide-in/up shown
      if (this.actions.DEAL__ARCHIVE === this.currentAction
        || (this.actions.DEAL__IN_PROGRESS === this.currentAction && (this.deal.visited_date === null || this.isBeforeToday(this.deal.visited_date.iso)))) {
        // Set null will block the slide-in/up to open but the action is processed by the component
        this.isOpened = null;
      }
      this.open();
    },
    close() {
      this.isOpened = false;
      this.currentAction = null;
      eventBus.dispatchEvent(new Event('messaging-action-hide'));
      this.progressBar = false;
    },
    open() {
      // When isOpened is null, the slide-in/up won't show but the component will process the action.
      if (this.isOpened !== null) {
        this.isOpened = true;
      }
    },
    setTitle(title) {
      this.title = title;
    },
    /**
     * @return String|null
     */
    getAction() {
      const { action } = this.payload;
      if (this.payload.toColumn === this.payload.fromColumn) {
        return null;
      }
      if (action && Object.values(this.actions).includes(action)) {
        return action;
      }
      if (this.payload.toColumn === COLUMN__IN_PROGRESS) {
        return this.actions.DEAL__IN_PROGRESS;
      }
      if (this.payload.toColumn === COLUMN__VISITED) {
        return this.actions.VISIT__SLOT_CHOICE;
      }
      if (this.payload.toColumn === COLUMN__SIGNED) {
        return this.actions.DEAL__SIGNED;
      }
      if (this.payload.toColumn === COLUMN__UNSIGNED) {
        return this.actions.DEAL__UNSIGNED;
      }
      return null;
    },
    setProgressBar(data) {
      this.hideProgressBar = false;
      this.progressBar = data.value;
      this.nbStep = data.nbStep;
      this.actualStep = data.actualStep;
    },
    hideProgressBar(value) {
      this.progressBarHidden = value;
    },
    changeStep() {
      this.actualStep -= 1;
      eventBus.dispatchEvent(new CustomEvent('formstepper-previous', { detail: this.actualStep }));
    },
  },
};
</script>
